import type { AreaStatus, IsoDate, RouteSettings, ScanEstimate, Suggestion } from '../../../shared/types';
import { apiFetch } from './api';

/** Plan tomorrow's API client (D82–D84). Planning and look-ups need the network; the accepted route
 *  itself is kept on the device and synced (see dayRoutes.ts). */

export type PlaceMark = 'square' | 'not_fit' | 'chain';

const area = (name: string) => `/api/daily/areas/${encodeURIComponent(name)}`;

/** A first plan in an area reads up to 40 websites; a look-up makes up to 300 Google calls. Both run
 *  well past the usual 20 s, and a request cut short would leave the server working unseen. */
const PLAN_TIMEOUT_MS = 90_000;
const SCAN_TIMEOUT_MS = 240_000;

/** `skipScan` ("Plan anyway") plans from the areas already looked up instead of asking for one. */
export const planDay = (
  day: IsoDate,
  exclude: readonly string[] = [],
  opts: { skipScan?: boolean; area?: string; includeLeadIds?: string[] } = {},
): Promise<Suggestion> =>
  apiFetch<Suggestion>(
    'POST',
    '/api/daily/plan',
    { day, exclude, ...(opts.skipScan ? { skip_scan: true } : {}),
      ...(opts.area ? { area: opts.area } : {}),
      ...(opts.includeLeadIds ? { include_lead_ids: opts.includeLeadIds } : {}) },
    { signal: AbortSignal.timeout(PLAN_TIMEOUT_MS) },
  );

export const getAreas = (): Promise<AreaStatus[]> => apiFetch<AreaStatus[]>('GET', '/api/daily/areas');

export const estimateArea = (name: string): Promise<ScanEstimate & { month_calls: number; cap: number }> =>
  apiFetch('GET', `${area(name)}/estimate`);

/** Looks the area up on Google; `error` says why it stopped short (the month's ceiling, a failure). */
export const scanArea = (name: string, quote: ScanEstimate): Promise<{ calls: number; places: number; error?: string }> =>
  apiFetch('POST', `${area(name)}/scan`, { confirmed_max_calls: quote.calls, restart: quote.state === 'stale' || quote.state === 'legacy-unknown', recover: quote.state === 'interrupted' }, { signal: AbortSignal.timeout(SCAN_TIMEOUT_MS) });

export const markPlace = (placeId: string, mark: PlaceMark): Promise<{ learned?: string }> =>
  apiFetch('POST', `/api/daily/places/${encodeURIComponent(placeId)}/mark`, { mark });

export const getSettings = async (): Promise<RouteSettings> => {
  const settings = await apiFetch<RouteSettings>('GET', '/api/daily/settings');
  return {...settings, warm_priority: settings.warm_priority === true};
};

export const putSettings = (
  patch: Partial<Omit<RouteSettings, 'updated_at' | 'home_lat' | 'home_lng'>>,
): Promise<RouteSettings> => apiFetch<RouteSettings>('PUT', '/api/daily/settings', patch);
